import React from "react"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../components/OnboardingComponents/CustomIconButton"
import InfiniteListSentinel from "../../components/Listing/InfiniteListSentinel"
import MediaFallbackImage from "../../components/Common/MediaFallbackImage"
import TruncatedText from "../../components/Common/TruncatedText"
import {
  Avatar,
  AvatarGroup,
} from "../../components/DashboardComponents/AvatarGroup"
import { ICONS } from "../../constants/icons"
import { getCallTypeBadgeLabel } from "../CallsForArtist/callMappers"

export const categoryClass = category =>
  `city-hub__tag city-hub__tag--${(category || "")
    .toLowerCase()
    .replace(/\s/g, "-")}`

export const responsePeople = (avatars = []) =>
  avatars.map((src, index) => ({
    name: `Respondent ${index + 1}`,
    src,
  }))

export const formatCreatorXp = xp =>
  `${Number(xp || 0).toLocaleString("en-US")}XP`

export const matchesCityHubSearch = (item, query) => {
  const q = String(query || "")
    .trim()
    .toLowerCase()
  if (!q) return true
  return (
    (item.title || "").toLowerCase().includes(q) ||
    (item.headline || "").toLowerCase().includes(q) ||
    (item.description || item.subtitle || item.text || item.excerpt || "")
      .toLowerCase()
      .includes(q) ||
    (item.photographer?.name || "").toLowerCase().includes(q) ||
    (item.name || "").toLowerCase().includes(q) ||
    (item.role || "").toLowerCase().includes(q) ||
    (item.poster?.name || "").toLowerCase().includes(q)
  )
}

export const matchesCityHubFilter = (item, filters = []) => {
  if (!filters.length) return true

  const categories = []
  if (Array.isArray(item.categoryNames)) {
    item.categoryNames.forEach(name => {
      if (name) categories.push(name)
    })
  }
  if (item.category) categories.push(item.category)
  if (Array.isArray(item.raw?.categories)) {
    item.raw.categories.forEach(cat => {
      const name = typeof cat === "string" ? cat : cat?.name
      if (name) categories.push(name)
    })
  }
  if (Array.isArray(item.tags)) {
    item.tags.forEach(tag => {
      const name = typeof tag === "string" ? tag : tag?.name
      if (name) categories.push(name)
    })
  }

  if (categories.length === 0) return false

  return categories.some(category => {
    const slug = String(category).toLowerCase().replace(/\s/g, "-")
    return filters.some(filter => {
      const f = String(filter).toLowerCase()
      return slug === f || slug.includes(f) || f.includes(slug)
    })
  })
}

export const EventCardSkeleton = () => (
  <article
    className="city-hub__event-card city-hub__event-card--skeleton"
    aria-hidden="true"
  >
    <div className="city-hub__event-heading">
      <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--sm" />
      <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--tag" />
      <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--tag" />
    </div>
    <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--title" />
    <div className="amok-skeleton city-hub__skeleton-line" />
    <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--md" />
    <div className="city-hub__event-footer">
      <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--footer" />
      <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--btn" />
    </div>
  </article>
)

export const OpportunityCardSkeleton = () => (
  <article
    className="city-hub__card city-hub__card--skeleton"
    aria-hidden="true"
  >
    <div className="city-hub__card-media">
      <div className="amok-skeleton city-hub__skeleton-img" />
    </div>
    <div className="city-hub__card-body">
      <div className="city-hub__card-content">
        <div className="city-hub__card-heading">
          <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--sm" />
          <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--tag" />
        </div>
        <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--title" />
        <div className="amok-skeleton city-hub__skeleton-line" />
        <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--md" />
        <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--location" />
      </div>
      <div className="city-hub__card-footer">
        <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--footer" />
        <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--btn" />
      </div>
    </div>
  </article>
)

export const OpportunityCard = ({
  item,
  onOpen,
  showLocation = true,
  variant = "calls",
}) => {
  const people =
    Array.isArray(item.responsePeople) && item.responsePeople.length > 0
      ? item.responsePeople
      : responsePeople(item.responseAvatars)
  const projectName = item.project?.name
  const cardTitle = item.headline || "N/A"
  const locationIcon = ICONS.callLocationGreen || ICONS.city
  const isInbox = variant === "inbox"
  const showAvatars = people.length > 0
  const typeKey = String(item.type || "")
    .trim()
    .toLowerCase()
  const typeLabel = getCallTypeBadgeLabel(item.type)

  return (
    <article
      className={`city-hub__card${isInbox ? " city-hub__card--inbox" : ""}`}
      onClick={onOpen}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onOpen()
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="city-hub__card-media">
        <MediaFallbackImage
          src={item.image}
          fallback={ICONS.thumbnailPlaceholder}
          alt=""
          className="city-hub__card-img"
        />
      </div>
      <div className="city-hub__card-body">
        <div className="city-hub__card-content">
          <div className="city-hub__card-heading">
            <div className="city-hub__card-heading-start">
              {projectName ? (
                <span className="city-hub__card-project">{projectName}</span>
              ) : null}
              {!isInbox && typeLabel ? (
                <span
                  className={`city-hub__type-badge city-hub__type-badge--${typeKey}`}
                >
                  {typeLabel}
                </span>
              ) : null}
              {isInbox && (item.categoryNames || []).length
                ? item.categoryNames.map(name => (
                    <span key={name} className={categoryClass(name)}>
                      {name}
                    </span>
                  ))
                : null}
            </div>
            {!isInbox && (item.categoryNames || []).length
              ? item.categoryNames.map(name => (
                  <span key={name} className={categoryClass(name)}>
                    {name}
                  </span>
                ))
              : null}
            {isInbox && showAvatars ? (
              <AvatarGroup
                people={people}
                size="xs"
                max={3}
                total={item.responses}
                className="city-hub__card-avatars"
              />
            ) : null}
          </div>
          <h3 className="city-hub__card-title">{cardTitle}</h3>
          <p className="city-hub__card-desc">{item.description}</p>
          {showLocation && item.city ? (
            <div className="city-hub__card-location">
              <img src={locationIcon} alt="" width={15} height={15} />
              <span>{item.city}</span>
            </div>
          ) : null}
        </div>

        <div className="city-hub__card-footer">
          <div className="city-hub__card-footer-meta">
            <div className="city-hub__card-posted">
              <span className="city-hub__card-posted-label">Posted by:</span>
              <span className="city-hub__card-posted-name">
                {item.poster?.name}
              </span>
              {isInbox && item.posted ? (
                <>
                  <span
                    className="city-hub__card-posted-sep"
                    aria-hidden="true"
                  >
                    -
                  </span>
                  <span className="city-hub__card-posted-time">
                    {item.posted}
                  </span>
                </>
              ) : null}
            </div>
            {!isInbox && showAvatars ? (
              <AvatarGroup
                people={people}
                size="xs"
                max={3}
                total={item.responses}
                className="city-hub__card-avatars"
              />
            ) : null}
          </div>
          <CustomButton
            variant="secondary"
            size="sm"
            className="city-hub__card-cta"
            trailingIconSrc={ICONS.arrowRightBlack}
            trailingIconAlt=""
            onClick={e => {
              e.stopPropagation()
              onOpen()
            }}
          >
            View Detail
          </CustomButton>
        </div>
      </div>
    </article>
  )
}

export const ReviewCardSkeleton = () => (
  <article
    className="city-hub__review-card city-hub__review-card--skeleton"
    aria-hidden="true"
  >
    <div className="amok-skeleton city-hub__review-media" />
    <div className="city-hub__review-body">
      <div className="city-hub__review-content">
        <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--title" />
        <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--footer" />
        <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--footer" />
      </div>
      <div className="city-hub__review-footer">
        <div className="city-hub__reviewer">
          <div className="amok-skeleton city-hub__reviewer-avatar" />
          <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--sm" />
        </div>
        <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--sm" />
      </div>
    </div>
  </article>
)

export const ReviewCard = ({
  review,
  canManage = false,
  onOpen,
  onEdit,
  onDelete,
}) => (
  <article
    className={`city-hub__review-card${
      onOpen ? " city-hub__review-card--clickable" : ""
    }`}
    role={onOpen ? "button" : undefined}
    tabIndex={onOpen ? 0 : undefined}
    aria-label={onOpen ? `View ${review.title || "review"}` : undefined}
    onClick={() => onOpen?.(review)}
    onKeyDown={event => {
      if (!onOpen) return
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        onOpen(review)
      }
    }}
  >
    <div className="city-hub__review-media">
      <MediaFallbackImage
        src={review.image || null}
        fallback={ICONS.thumbPlaceholder}
        alt=""
        className="city-hub__review-img"
      />
      {canManage ? (
        <div
          className="city-hub__review-actions"
          onClick={event => event.stopPropagation()}
          onMouseDown={event => event.stopPropagation()}
        >
          <CustomIconButton
            iconSrc={ICONS.edit}
            label="Edit review"
            size="sm"
            onClick={event => {
              event.stopPropagation()
              onEdit?.(review)
            }}
          />
          <CustomIconButton
            iconSrc={ICONS.trash}
            label="Delete review"
            tone="danger"
            size="sm"
            onClick={event => {
              event.stopPropagation()
              onDelete?.(review)
            }}
          />
        </div>
      ) : null}
    </div>
    <div className="city-hub__review-body">
      <div className="city-hub__review-content">
        <h3 className="city-hub__review-title">{review.title}</h3>
        <p className="city-hub__review-text">{review.text}</p>
      </div>
      <div className="city-hub__review-footer">
        <div className="city-hub__reviewer">
          <MediaFallbackImage
            src={review.reviewer?.avatar || null}
            fallback={ICONS.thumbPlaceholder}
            alt=""
            className="city-hub__reviewer-avatar"
          />
          <span className="city-hub__reviewer-name">
            {review.reviewer?.name || "—"}
          </span>
        </div>
        <span className="city-hub__review-date">{review.date}</span>
      </div>
    </div>
  </article>
)

export const PhotoCardSkeleton = () => (
  <article
    className="city-hub__photo-card city-hub__photo-card--skeleton"
    aria-hidden="true"
  >
    <div className="amok-skeleton city-hub__photo-frame" />
    <div className="city-hub__photo-content">
      <div className="city-hub__photo-top">
        <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--title" />
        <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--sm" />
      </div>
      <div className="amok-skeleton city-hub__skeleton-line city-hub__skeleton-line--footer" />
    </div>
  </article>
)

export const PhotoCard = ({
  photo,
  canManage = false,
  isEditLoading = false,
  isLiking = false,
  onOpen,
  onEdit,
  onDelete,
  onToggleLike,
}) => (
  <article className="city-hub__photo-card">
    <div
      className={`city-hub__photo-frame${
        onOpen ? " city-hub__photo-frame--clickable" : ""
      }`}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `View ${photo.title || "photography"}` : undefined}
      onClick={() => onOpen?.(photo)}
      onKeyDown={event => {
        if (!onOpen) return
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onOpen(photo)
        }
      }}
    >
      {photo.src ? (
        photo.isVideo ? (
          <video
            className="city-hub__photo-media"
            src={photo.src}
            muted
            playsInline
            preload="metadata"
          />
        ) : (
          <MediaFallbackImage
            className="city-hub__photo-media"
            src={photo.src}
            fallback={ICONS.thumbPlaceholder}
            alt={photo.title}
          />
        )
      ) : (
        <MediaFallbackImage
          className="city-hub__photo-media"
          src={null}
          fallback={ICONS.thumbPlaceholder}
          alt=""
        />
      )}
      {canManage ? (
        <div className="city-hub__photo-actions">
          <CustomIconButton
            iconSrc={ICONS.edit}
            label="Edit photo"
            size="sm"
            loading={isEditLoading}
            onClick={event => {
              event.stopPropagation()
              onEdit?.(photo)
            }}
          />
          <CustomIconButton
            iconSrc={ICONS.trash}
            label="Delete photo"
            tone="danger"
            size="sm"
            onClick={event => {
              event.stopPropagation()
              onDelete?.(photo)
            }}
          />
        </div>
      ) : null}
    </div>
    <div className="city-hub__photo-content">
      <div className="city-hub__photo-top">
        <h3 className="city-hub__photo-title">{photo.title}</h3>
        <span className="city-hub__photo-date">{photo.date}</span>
      </div>
      <div className="city-hub__photo-footer">
        <div className="city-hub__photo-photographer">
          <Avatar
            src={photo.photographer?.avatar}
            name={photo.photographer?.name}
            size="xs"
          />
          <span className="city-hub__photo-photographer-name">
            {photo.photographer?.name || "—"}
          </span>
        </div>
        <button
          type="button"
          className={`city-hub__photo-likes${
            photo.liked ? " city-hub__photo-likes--liked" : ""
          }`}
          aria-pressed={Boolean(photo.liked)}
          aria-label={photo.liked ? "Unlike photo" : "Like photo"}
          disabled={isLiking}
          onClick={event => {
            event.stopPropagation()
            onToggleLike?.(photo)
          }}
        >
          <img
            src={photo.liked ? ICONS.heartFill : ICONS.heartOutlineRed}
            alt=""
            width={20}
            height={20}
            className="city-hub__photo-heart"
          />
          <span>{photo.likes || 0}</span>
        </button>
      </div>
    </div>
  </article>
)

export const CreatorsTable = ({ creators, onViewProfile }) => (
  <div className="city-hub__creators-wrap">
    <div className="city-hub__creators-table" role="table">
      <div className="city-hub__creators-head" role="row">
        <div role="columnheader">Creator Name</div>
        <div role="columnheader">Role</div>
        <div role="columnheader">XP</div>
        <div role="columnheader">Projects</div>
        <div role="columnheader">Joined</div>
        <div className="city-hub__creators-actions-head" role="columnheader">
          Actions
        </div>
      </div>
      {creators.map(creator => (
        <div key={creator.id} className="city-hub__creators-row" role="row">
          <div className="city-hub__creators-name" role="cell">
            <Avatar
              src={creator.avatar}
              name={creator.name}
              size="sm"
              className="city-hub__creators-avatar"
            />
            <TruncatedText>{creator.name}</TruncatedText>
          </div>
          <div role="cell">
            <TruncatedText>{creator.role}</TruncatedText>
          </div>
          <div role="cell">
            <TruncatedText>{formatCreatorXp(creator.xp)}</TruncatedText>
          </div>
          <div role="cell">
            <TruncatedText>{creator.projects}</TruncatedText>
          </div>
          <div role="cell">
            <TruncatedText>{creator.joined}</TruncatedText>
          </div>
          <div className="city-hub__creators-actions" role="cell">
            <CustomButton
              variant="toned"
              size="sm"
              className="city-hub__creators-profile"
              trailingIconSrc={ICONS.arrowRight12}
              trailingIconAlt=""
              onClick={() => onViewProfile?.(creator)}
            >
              View Profile
            </CustomButton>
          </div>
        </div>
      ))}
    </div>
  </div>
)

export const CityHubInfiniteSentinel = ({ sentinelRef, hasMore }) => (
  <InfiniteListSentinel
    sentinelRef={sentinelRef}
    hasMore={hasMore}
    className="city-hub__infinite-sentinel"
  />
)
